import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/Layout/AdminLayout';
import { api } from '../../services/api';
import type { ScoringRules, ScoringRulesUpdateRequest } from '@vedjegy/shared';


type FormState = {
  passThresholdNew: string;
  passThresholdRenewal: string;
  maxBaseScore: string;
  maxBonusScore: string;
  excellentThreshold: string;
  excellentBonusMin: string;
};

function rulestoForm(r: ScoringRules): FormState {
  return {
    passThresholdNew:    String(Math.round(r.passThresholdNew    * 100)),
    passThresholdRenewal: String(Math.round(r.passThresholdRenewal * 100)),
    maxBaseScore:        String(r.maxBaseScore),
    maxBonusScore:       String(r.maxBonusScore),
    excellentThreshold:  String(Math.round(r.excellentThreshold  * 100)),
    excellentBonusMin:   String(r.excellentBonusMin),
  };
}

const EMPTY_FORM: FormState = {
  passThresholdNew: '60',
  passThresholdRenewal: '60',
  maxBaseScore: '150',
  maxBonusScore: '10',
  excellentThreshold: '90',
  excellentBonusMin: '5',
};

export default function AdminScoringRulesPage() {
  const [yearOptions, setYearOptions] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [form, setForm]     = useState<FormState>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [notFound, setNotFound] = useState(false);

  // Load available years on mount; default to the first (highest = next year)
  useEffect(() => {
    api.scoringRules.getYears()
      .then(({ years }) => {
        setYearOptions(years);
        if (years.length > 0) setSelectedYear(years[0]);
      })
      .catch(() => setError('Nem sikerült betölteni az éveket.'));
  }, []);

  const load = useCallback(async (year: number) => {
    setLoading(true);
    setError('');
    setSuccess('');
    setDirty(false);
    setNotFound(false);
    try {
      const rules = await api.scoringRules.get(year);
      setForm(rulestoForm(rules));
    } catch (e: unknown) {
      const status = (e as { status?: number })?.status;
      if (status === 404) {
        // No rules yet for this year — show defaults
        setForm(EMPTY_FORM);
        setNotFound(true);
      } else {
        setError('Nem sikerült betölteni a pontozási szabályokat.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (selectedYear !== null) void load(selectedYear); }, [load, selectedYear]);

  function setField(key: keyof FormState, value: string) {
    setForm((p) => ({ ...p, [key]: value }));
    setDirty(true);
    setSuccess('');
  }

  async function handleSave() {
    if (selectedYear === null) return;
    setError('');
    setSuccess('');
    // Validate
    const ptn = parseInt(form.passThresholdNew, 10);
    const ptr = parseInt(form.passThresholdRenewal, 10);
    const et  = parseInt(form.excellentThreshold, 10);
    const mbs = parseInt(form.maxBaseScore, 10);
    const mbo = parseInt(form.maxBonusScore, 10);
    const ebm = parseInt(form.excellentBonusMin, 10);
    if ([ptn, ptr, et, mbs, mbo, ebm].some(isNaN)) {
      setError('Minden mező kitöltése kötelező (számok).'); return;
    }
    if (ptn < 0 || ptn > 100 || ptr < 0 || ptr > 100 || et < 0 || et > 100) {
      setError('A százalékos értékek 0–100 közé kell essenek.'); return;
    }
    const payload: ScoringRulesUpdateRequest = {
      year:                 selectedYear,
      passThresholdNew:     ptn / 100,
      passThresholdRenewal: ptr / 100,
      maxBaseScore:         mbs,
      maxBonusScore:        mbo,
      excellentThreshold:   et / 100,
      excellentBonusMin:    ebm,
    };
    setSaving(true);
    try {
      await api.scoringRules.update(payload);
      setDirty(false);
      setNotFound(false);
      setSuccess('Pontozási szabályok elmentve.');
    } catch {
      setError('Mentés sikertelen.');
    } finally {
      setSaving(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6,
    fontSize: 14, width: '100%', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4,
  };
  const hintStyle: React.CSSProperties = {
    fontSize: 11, color: '#9ca3af', marginTop: 3,
  };

  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2937', marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid #e5e7eb' }}>
        {title}
      </div>
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        {children}
      </div>
    </div>
  );

  const Field = ({ label, hint, fieldKey, suffix }: { label: string; hint?: string; fieldKey: keyof FormState; suffix?: string }) => (
    <div style={{ minWidth: 180, flex: 1 }}>
      <label style={labelStyle}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input
          style={{ ...inputStyle, flex: 1 }}
          type="number" min={0}
          value={form[fieldKey]}
          onChange={(e) => setField(fieldKey, e.target.value)}
        />
        {suffix && <span style={{ fontSize: 14, color: '#6b7280', whiteSpace: 'nowrap' }}>{suffix}</span>}
      </div>
      {hint && <div style={hintStyle}>{hint}</div>}
    </div>
  );

  return (
    <AdminLayout>
      <div style={{ padding: 28, maxWidth: 2000 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Pontozási szabályok</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 600 }}>Év:</span>
            <select
              value={selectedYear ?? ''}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
              style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
            >
              {yearOptions.map((y: number) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        {error && (
          <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, color: '#dc2626', fontSize: 13 }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, color: '#16a34a', fontSize: 13 }}>
            {success}
          </div>
        )}
        {notFound && !loading && (
          <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, color: '#92400e', fontSize: 13 }}>
            Nincs elmentett szabály a(z) {selectedYear}. évre — az alapértelmezett értékek vannak feltöltve. Mentés után jön létre a rekord.
          </div>
        )}

        {loading ? (
          <div style={{ color: '#9ca3af', fontSize: 14 }}>Betöltés…</div>
        ) : (
          <>
            <Section title="Megfelelési küszöbök">
              <Field
                fieldKey="passThresholdNew"
                label="Új pályázat — átmenési küszöb"
                hint="Az összesített alappontszám %-ában"
                suffix="%"
              />
              <Field
                fieldKey="passThresholdRenewal"
                label="Megújítás — átmenési küszöb"
                hint="Az összesített alappontszám %-ában"
                suffix="%"
              />
            </Section>

            <Section title="Maximális pontszámok">
              <Field
                fieldKey="maxBaseScore"
                label="Max. alappontszám"
                hint="Összes nem-bónusz szempont összege"
                suffix="pont"
              />
              <Field
                fieldKey="maxBonusScore"
                label="Max. bónuszpontszám"
                hint="Összes bónusz szempont összege"
                suffix="pont"
              />
            </Section>

            <Section title="Kiváló pályázó feltételek">
              <Field
                fieldKey="excellentThreshold"
                label="Kiváló küszöb"
                hint="Az alappontszám %-a (pl. 90 = 90%)"
                suffix="%"
              />
              <Field
                fieldKey="excellentBonusMin"
                label="Min. bónuszpont"
                hint="Legalább ennyi bónuszpontot kell elérni"
                suffix="pont"
              />
            </Section>

            <div style={{ padding: '14px 16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 24, fontSize: 13, color: '#475569' }}>
              <strong>Kiváló pályázó feltételek:</strong> Átlagos alappontszám ≥ küszöb% × max. alappontszám,
              ÉS átlagos bónuszpont ≥ min. bónuszpont,
              ÉS egyetlen bírált szempont sem esik a saját max. pontszámának 50% alá.
            </div>

            <button
              onClick={() => void handleSave()}
              disabled={saving || !dirty}
              style={{
                padding: '10px 32px', background: dirty ? '#0ea5e9' : '#e5e7eb',
                color: dirty ? '#fff' : '#9ca3af',
                border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 14,
                cursor: dirty ? 'pointer' : 'default',
              }}
            >
              {saving ? 'Mentés…' : 'Szabályok mentése'}
            </button>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
